/**
 * Vibesite Event Bus
 *
 * Tiny in-memory pub/sub for canonical user-action events. Lives inside the
 * VibesitesProvider; one instance per page. Source blocks (lead form, tour
 * scheduler) and the DOM-capture layer call `emit()`; tracking blocks (GA4,
 * Facebook Pixel, GTM) call `subscribe()`.
 *
 * Design constraints:
 *   - Synchronous fan-out. emit() walks subscribers in subscription order; if
 *     one throws, we catch + log and keep going so a broken sink can't take
 *     down the others.
 *   - Late-mount safe. A bounded ring buffer of recent events lets a tracking
 *     block that mounts a tick after a source emit (e.g. page-view fired in a
 *     mount effect) still receive it.
 *   - Pure JS. No window/document touched here so the bus is safe to construct
 *     during SSR. The DOM-capture listener that translates real clicks into
 *     emit() calls lives in dom-capture.ts and is mounted via useEffect.
 *   - No persistence. Network-side delivery (queueing, retry, sendBeacon) is
 *     owned by the platform SDKs (gtag, fbq, GTM dataLayer consumers). Adding
 *     another queue here would conflict with their built-in mechanisms.
 */

import {
  isVibesiteEventName,
  type VibesiteEventName,
  type VibesiteEventPayload,
  type VibesiteEventPayloadMap,
} from './catalog';

// ============================================================================
// Types
// ============================================================================

/**
 * A subscriber callback. Receives the canonical event name and a payload that
 * is typed to the specific event when subscribing to a single name, or the
 * full union when subscribing to '*'.
 */
export type VibesiteEventListener<N extends VibesiteEventName | '*' = '*'> = (
  name: N extends VibesiteEventName ? N : VibesiteEventName,
  payload: N extends VibesiteEventName
    ? VibesiteEventPayloadMap[N]
    : VibesiteEventPayload,
) => void;

/**
 * The unsubscribe function returned by `subscribe()`. Idempotent.
 */
export type Unsubscribe = () => void;

/**
 * Public bus interface — exposed via `useVibesiteEvents()` and (eventually)
 * `window.vibesites.events`.
 */
export interface VibesiteEventBus {
  /**
   * Emit an event. Calls every matching subscriber synchronously. Unknown
   * event names are warned-and-dropped so a typo can't silently feed garbage
   * to GA4 / Facebook.
   */
  emit<N extends VibesiteEventName>(
    name: N,
    payload?: VibesiteEventPayloadMap[N],
  ): void;

  /**
   * Subscribe to a single canonical event, or '*' for every event. Returns
   * an unsubscribe function. Subscribers added after an event has already
   * fired will receive any matching events still in the replay buffer.
   */
  subscribe<N extends VibesiteEventName | '*'>(
    name: N,
    listener: VibesiteEventListener<N>,
  ): Unsubscribe;
}

// ============================================================================
// Implementation
// ============================================================================

/**
 * Maximum number of recent events to retain for late subscribers. ~32 covers
 * realistic page sessions (a few CTAs per page) without unbounded growth.
 */
const REPLAY_BUFFER_SIZE = 32;

interface BufferedEvent {
  name: VibesiteEventName;
  payload: VibesiteEventPayload;
}

/**
 * Create a new event bus instance. One per VibesitesProvider.
 */
export function createEventBus(): VibesiteEventBus {
  // Subscribers keyed by event name. The wildcard set lives under '*'.
  // Using Set so unsubscribe is O(1) and double-subscribe is a no-op.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const subscribers = new Map<VibesiteEventName | '*', Set<VibesiteEventListener<any>>>();

  // Bounded ring buffer for late-mount catch-up. We push to the end and
  // shift from the front; size is bounded by REPLAY_BUFFER_SIZE.
  const replayBuffer: BufferedEvent[] = [];

  const bus: VibesiteEventBus = {
    emit(name, payload) {
      if (!isVibesiteEventName(name)) {
        // Warn-and-drop for typos / non-catalog names. We don't throw because
        // analytics calls should never break the site.
        // eslint-disable-next-line no-console
        console.warn(
          `[vibesite-events] Dropped unknown event "${String(name)}" — not in catalog.`,
        );
        return;
      }

      const fullPayload = (payload ?? {}) as VibesiteEventPayload;

      // Buffer the event for any subscribers that mount later this page.
      replayBuffer.push({ name, payload: fullPayload });
      if (replayBuffer.length > REPLAY_BUFFER_SIZE) replayBuffer.shift();

      // Snapshot subscriber lists before invoking so a subscriber that
      // unsubscribes itself during fan-out doesn't mutate the iteration.
      const named = subscribers.get(name);
      const wildcard = subscribers.get('*');
      const callList: Array<VibesiteEventListener<any>> = [];
      if (named) callList.push(...named);
      if (wildcard) callList.push(...wildcard);

      for (const listener of callList) {
        try {
          listener(name as any, fullPayload as any);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(
            `[vibesite-events] Subscriber threw for event "${name}":`,
            err,
          );
        }
      }
    },

    subscribe(name, listener) {
      let set = subscribers.get(name);
      if (!set) {
        set = new Set();
        subscribers.set(name, set);
      }
      set.add(listener as VibesiteEventListener<any>);

      // Replay any matching events already in the buffer so this subscriber
      // doesn't miss things that fired during earlier mount effects.
      // Snapshot first so a listener that emits new events during replay
      // doesn't see itself replay-loop.
      const buffered = replayBuffer.slice();
      for (const evt of buffered) {
        if (name === '*' || evt.name === name) {
          try {
            (listener as VibesiteEventListener<any>)(evt.name as any, evt.payload as any);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error(
              `[vibesite-events] Subscriber threw during replay for event "${evt.name}":`,
              err,
            );
          }
        }
      }

      let unsubscribed = false;
      return () => {
        if (unsubscribed) return;
        unsubscribed = true;
        const current = subscribers.get(name);
        if (!current) return;
        current.delete(listener as VibesiteEventListener<any>);
        if (current.size === 0) subscribers.delete(name);
      };
    },
  };

  return bus;
}
